import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy:    "#0d1b2a",
  gold:    "#c9a96e",
  white:   "#ffffff",
  bg:      "#f7f5f1",
  slate:   "#5c6b7e",
  slateLt: "#9ca8b7",
  line:    "#eeebe6",
  peach:   "#ffebd3",
  inputBg: "#f8f7f5",
} as const;

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n: number) => String(n).padStart(2, "0");

/* ═══════════════════════════════════════════════════════════════════════════
   PROPS
   ═══════════════════════════════════════════════════════════════════════════ */
interface Props {
  value: string;                          // "YYYY-MM-DD" or ""
  onChange: (value: string) => void;
  label?: string;
  error?: boolean;
  size?: "small" | "medium";
  sx?: object;
  minDate?: string;                       // "YYYY-MM-DD"
  placeholder?: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const PremiumDatePicker: React.FC<Props> = ({
  value, onChange, label = "Date", error = false,
  size = "small", sx = {}, minDate, placeholder = "Select date",
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // Parse current value or default to today's month
  const parsed = useMemo(() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      return { year: y, month: m - 1 }; // month is 0-indexed
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  }, [value]);

  const [viewYear, setViewYear] = useState(parsed.year);
  const [viewMonth, setViewMonth] = useState(parsed.month);
  const [hovered, setHovered] = useState<string | null>(null);

  // Sync view when value changes externally
  React.useEffect(() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      setViewYear(y);
      setViewMonth(m - 1);
    }
  }, [value]);

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }, []);

  // Navigation
  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  // Calendar grid
  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthLabel = `${MONTHS[viewMonth]} ${viewYear}`;

  const isDisabled = useCallback((dateStr: string) => {
    if (!minDate) return false;
    return dateStr < minDate;
  }, [minDate]);

  const handleSelect = (day: number) => {
    const dateStr = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`;
    if (isDisabled(dateStr)) return;
    onChange(dateStr);
    setOpen(false);
  };

  const handleToday = () => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    onChange(today);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  // Display value
  const displayValue = useMemo(() => {
    if (!value) return "";
    const d = new Date(value + "T12:00:00");
    return d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  }, [value]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: "relative" }}>
        {/* Input */}
        <TextField
          label={label}
          fullWidth
          size={size}
          value={displayValue}
          placeholder={placeholder}
          onClick={() => setOpen(o => !o)}
          error={error}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CalendarTodayOutlinedIcon sx={{ fontSize: 17, color: T.slateLt, cursor: "pointer" }} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: !!displayValue }}
          sx={{
            ...sx,
            cursor: "pointer",
            "& .MuiInputBase-input": { cursor: "pointer" },
          }}
        />

        {/* Calendar dropdown */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1500 }}
        >
          <Box sx={{
            mt: 1, bgcolor: T.white, borderRadius: "14px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 12px 40px rgba(0,0,0,0.04)",
            overflow: "hidden", width: 310, userSelect: "none",
          }}>
            {/* ── Month header ── */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 2, py: 1.5,
            }}>
              <IconButton size="small" onClick={prevMonth}
                sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: T.bg, "&:hover": { bgcolor: T.peach } }}>
                <ChevronLeftIcon sx={{ fontSize: 18, color: T.navy }} />
              </IconButton>

              <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }}>
                {monthLabel}
              </Typography>

              <IconButton size="small" onClick={nextMonth}
                sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: T.bg, "&:hover": { bgcolor: T.peach } }}>
                <ChevronRightIcon sx={{ fontSize: 18, color: T.navy }} />
              </IconButton>
            </Box>

            {/* ── Weekday headers ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", px: 1.5, pb: 0.5 }}>
              {DOW.map(d => (
                <Box key={d} sx={{ textAlign: "center", py: 0.5 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {d}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* ── Date grid ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", px: 1.5, pb: 1.5, gap: "2px" }}>
              {/* Empty cells */}
              {Array.from({ length: firstDow }).map((_, i) => (
                <Box key={`e-${i}`} sx={{ width: 38, height: 38 }} />
              ))}

              {/* Day cells */}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dateStr = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`;
                const isSelected = dateStr === value;
                const isToday = dateStr === today;
                const disabled = isDisabled(dateStr);
                const isHov = hovered === dateStr && !disabled && !isSelected;

                return (
                  <Box
                    key={day}
                    onClick={() => !disabled && handleSelect(day)}
                    onMouseEnter={() => setHovered(dateStr)}
                    onMouseLeave={() => setHovered(null)}
                    sx={{
                      width: 38, height: 38, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: disabled ? "default" : "pointer",
                      opacity: disabled ? 0.3 : 1,
                      transition: "all 0.15s ease",
                      bgcolor: isSelected ? T.navy : isHov ? T.peach : isToday ? T.bg : "transparent",
                      boxShadow: isSelected ? "0 2px 8px rgba(13,27,42,0.2)" : "none",
                      transform: isSelected ? "scale(1)" : isHov ? "scale(1.05)" : "scale(1)",
                      border: isToday && !isSelected ? `1.5px solid ${T.gold}` : "1.5px solid transparent",
                      "&:hover": !disabled && !isSelected ? {
                        bgcolor: T.peach,
                        transform: "scale(1.05)",
                      } : {},
                    }}
                  >
                    <Typography sx={{
                      fontSize: 13, fontWeight: isSelected || isToday ? 700 : 500,
                      color: isSelected ? T.white : isToday ? T.gold : T.navy,
                      lineHeight: 1,
                    }}>
                      {day}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* ── Footer ── */}
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: 2, py: 1.2, borderTop: `1px solid ${T.line}`, bgcolor: T.bg,
            }}>
              <Typography
                onClick={handleToday}
                sx={{
                  fontSize: 12, fontWeight: 600, color: T.gold, cursor: "pointer",
                  "&:hover": { color: T.navy },
                  transition: "color 0.15s",
                }}
              >
                Today
              </Typography>
              <Typography
                onClick={handleClear}
                sx={{
                  fontSize: 12, fontWeight: 500, color: T.slateLt, cursor: "pointer",
                  "&:hover": { color: T.navy },
                  transition: "color 0.15s",
                }}
              >
                Clear
              </Typography>
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default PremiumDatePicker;
